import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pin, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, formatDateTime } from "../ui";
import { useAuth } from "../AuthContext";

const CATS = ["Dúvida", "Sugestão", "Experiência", "Outro"];

export default function Forum() {
  const { user } = useAuth();
  const [topics, setTopics] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "Dúvida" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/portal/forum")
      .then((r) => setTopics(r.data))
      .catch(() => toast.error("Erro ao carregar tópicos."));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.length < 3) {
      toast.error("Título deve ter ao menos 3 caracteres");
      return;
    }
    if (!form.body.trim() || form.body.length < 3) {
      toast.error("Conteúdo é obrigatório");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/forum", form);
      toast.success("Tópico criado!");
      setOpen(false);
      setForm({ title: "", body: "", category: "Dúvida" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteTopic = async (id) => {
    if (!window.confirm("Excluir este tópico e suas respostas?")) return;
    try {
      await api.delete(`/admin/forum/${id}`);
      toast.success("Tópico removido");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePin = async (t) => {
    try {
      await api.patch(`/admin/forum/${t.id}`, { pinned: !t.pinned });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const baseUrl = user?.role === "admin" ? "/admin/forum" : "/portal/forum";

  return (
    <div data-testid="forum-list">
      <PageHeader
        title="Fórum dos Associados"
        subtitle="Tire dúvidas, compartilhe experiências e discuta propostas."
        actions={
          user?.role !== "admin" && (
            <button
              data-testid="new-topic-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
            >
              <Plus size={14} /> Novo tópico
            </button>
          )
        }
      />

      {open && (
        <Card className="p-5 mb-5">
          <form data-testid="new-topic-form" onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Título *
              </label>
              <input
                data-testid="new-topic-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
                required
                minLength={3}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Categoria *
              </label>
              <select
                data-testid="new-topic-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
              >
                {CATS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Conteúdo *
              </label>
              <textarea
                data-testid="new-topic-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[120px]"
                required
                minLength={3}
              />
            </div>
            <div className="flex gap-2">
              <button
                data-testid="new-topic-submit"
                type="submit"
                disabled={busy}
                className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                Publicar
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
              >
                Cancelar
              </button>
            </div>
          </form>
        </Card>
      )}

      {topics === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : topics.length === 0 ? (
        <EmptyState title="Nenhum tópico ainda" hint="Crie o primeiro." />
      ) : (
        <Card>
          <ul className="divide-y divide-slate-200">
            {topics.map((t) => (
              <li
                key={t.id}
                data-testid={`topic-row-${t.id}`}
                className="p-5 hover:bg-slate-50 flex items-start gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.pinned && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#FEDF00] bg-[#0A0F14] px-2 py-0.5">
                        <Pin size={10} /> Fixado
                      </span>
                    )}
                    <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] uppercase tracking-wide">
                      {t.category}
                    </span>
                  </div>
                  <Link
                    to={`${baseUrl}/${t.id}`}
                    className="block mt-2 font-serif-display text-xl hover:text-[#002776]"
                  >
                    {t.title}
                  </Link>
                  <div className="mt-1 text-xs text-slate-500">
                    por {t.author_name} · {formatDateTime(t.created_at)}
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <MessageSquare size={12} /> {t.reply_count || 0}
                  </div>
                  {user?.role === "admin" && (
                    <div className="flex gap-1">
                      <button
                        data-testid={`pin-${t.id}`}
                        onClick={() => togglePin(t)}
                        className="px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#FEDF00]"
                      >
                        {t.pinned ? "Desafixar" : "Fixar"}
                      </button>
                      <button
                        data-testid={`delete-topic-${t.id}`}
                        onClick={() => deleteTopic(t.id)}
                        className="px-2 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                      >
                        Excluir
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
